import { Form, Formik } from "formik";
import { Button } from "@/components/base/Button.tsx";
import { FormikSelect } from "@/components/common/form/FomikSelect.tsx";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FormikInput } from "@/components/common/form/FormikInput.tsx";
import * as yup from "yup";
import { tenant } from "@/repositories/tenant.ts";
import { useStore } from "@/stores/useStore.ts";
import { Error } from "@/components/base/Error.tsx";

type SetupInfo = {
  accountType: string;
  joinCode?: string;
  name?: string;
};

const step2ValidationUser = yup.object({
  joinCode: yup
    .string()
    .required("Join code is required")
    .matches(/^\w{4}-\w{4}$/, "Join code must be in the format XXXX-XXXX")
    .length(9, "Join code must be in the format XXXX-XXXX"),
});

const step2ValidationCompany = yup.object({
  name: yup.string().required("Name is required"),
});

export const SetupForm = () => {
  const userId = useStore((s) => s.user?.id);
  const updateAllowedTenants = useStore((s) => s.updateAllowedTenants);
  const [searchParams, setSearchParams] = useSearchParams();
  const [setupInfo, setSetupInfo] = useState<SetupInfo>();
  const [step, setStep] = useState<number>(0);
  const [joinError, setJoinError] = useState<string>();

  useEffect(() => {
    setStep(parseInt(searchParams.get("step") ?? "1"));
  }, [searchParams]);

  if (!userId) {
    return null;
  }

  const handleNextClick = (values: SetupInfo) => {
    setSetupInfo(values);
    setSearchParams((params) => {
      params.set("step", `${step + 1}`);
      return params;
    });
  };

  const handleBackClick = () => {
    setSearchParams((params) => {
      params.set("step", `${step - 1}`);
      return params;
    });
  };

  const handleCreateCompany = async () => {
    if (!setupInfo?.name) {
      return;
    }
    tenant.create({ name: setupInfo.name }, userId).then(async (t) => {
      await updateAllowedTenants();
      return t;
    });
  };

  const handleJoinCompany = async (joinCode: string) => {
    setJoinError(undefined);
    const tenantsToJoin = await tenant.getTenantsByCode(joinCode);
    if (!tenantsToJoin || tenantsToJoin.length === 0) {
      setJoinError(
        "No companies were found with that code. Please contact your admin."
      );
      return;
    }
    const joinTenantId = tenantsToJoin[0].id;
    await tenant
      .addUser(joinTenantId, userId, "user", joinCode)
      .then(() => updateAllowedTenants());
  };

  return (
    <>
      {step === 1 && (
        <Formik
          initialValues={{ accountType: "user" }}
          onSubmit={(values) => handleNextClick(values)}
          validateOnMount={true}
        >
          {({ isValid }) => (
            <Form className="flex flex-col gap-4 m-auto w-64">
              <div>
                <span className="font-bold">Step 1:</span> Select the type of
                account you will use.
              </div>
              <FormikSelect
                name="accountType"
                id="accountType"
                label="Type of Account"
                options={[
                  { value: "user", label: "User" },
                  { value: "company", label: "Company" },
                ]}
              />

              <Button disabled={!isValid} type="submit">
                Submit
              </Button>
            </Form>
          )}
        </Formik>
      )}
      {step === 2 && setupInfo?.accountType === "user" && (
        <Formik
          initialValues={{ joinCode: "" }}
          validationSchema={step2ValidationUser}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            setSetupInfo({ ...setupInfo, joinCode: values.joinCode });
            handleJoinCompany(values.joinCode).finally(() =>
              setSubmitting(false)
            );
          }}
          validateOnMount={true}
        >
          {({ isValid, isSubmitting }) => (
            <Form className="flex flex-col gap-4 m-auto w-64">
              <div>
                <span className="font-bold">Step 2:</span>Great! The company you
                wish to join should have given you a code to join. Enter it
                here, or ask you administrator for it.
              </div>
              <FormikInput
                name="joinCode"
                id="joinCode"
                label="Company Code"
                type="text"
              />
              {joinError && <Error error={joinError} />}
              <Button disabled={!isValid || isSubmitting} type="submit">
                {isSubmitting ? "Submitting" : "Submit"}
              </Button>
            </Form>
          )}
        </Formik>
      )}
      {step === 2 && setupInfo?.accountType === "company" && (
        <Formik
          initialValues={{ name: setupInfo.name ? setupInfo.name : "" }}
          validationSchema={step2ValidationCompany}
          onSubmit={(values) => handleNextClick({ ...setupInfo, ...values })}
          validateOnMount={true}
        >
          {({ isValid }) => (
            <Form className="flex flex-col gap-4 m-auto w-64">
              <div>
                <span className="font-bold">Step 2:</span>Awesome! Let's get
                your company started using RouteMe.
              </div>
              <FormikInput
                name="name"
                id="name"
                label="Company Name"
                type="text"
              />
              <Button disabled={!isValid} type="submit">
                Submit
              </Button>
            </Form>
          )}
        </Formik>
      )}

      {step === 3 && setupInfo?.accountType === "company" && (
        <Formik
          initialValues={{ name: setupInfo.name }}
          onSubmit={() => handleCreateCompany()}
          validateOnMount={true}
        >
          {({ isValid }) => (
            <Form className="flex flex-col gap-4 m-auto w-64">
              <div>
                <span className="font-bold">Confirm:</span> We will now create
                your company. Verify the name of your company. This cannot be
                changed.
              </div>
              <FormikInput
                name="name"
                id="name"
                label="Company Name"
                type="text"
                disabled
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleBackClick}
              >
                Go Back
              </Button>
              <Button disabled={!isValid} type="submit">
                Submit
              </Button>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
